import { useCallback, useEffect, useRef } from "react";
import type { Celebrity } from "@pushup/types";
import { useWorkoutStore } from "@/state/workout-store";
import { useClipRecorder, type ClipRecordingResult } from "./use-clip-recorder";
import { drawCompositeFrame, type CompositeLandmark } from "./composite-frame";

export interface RecordingCompositeDeps {
  /** Live camera preview element to stamp into the recorded frame. */
  getVideo: () => HTMLVideoElement | null;
  /** Most recent detected pose landmarks (normalized), or null. */
  getLandmarks: () => readonly CompositeLandmark[] | null;
  /** Currently selected opponent, if any. */
  getCelebrity: () => Celebrity | null;
  /** Whether the round clock has hit zero (recording tail running). */
  getRoundEnded: () => boolean;
}

const COMPOSITE_WIDTH = 1280;
const COMPOSITE_HEIGHT = 720;
const COMPOSITE_FPS = 30;

/**
 * Records a clip that matches what the user sees on screen: the (mirrored)
 * camera feed, the pose skeleton overlay, and the HUD timer/scoreboard. It
 * composes those into an offscreen canvas and records the canvas stream (plus
 * the mic audio from the source stream) instead of the raw camera stream.
 *
 * Wraps `useClipRecorder` and exposes the same API, so callers can swap the
 * raw-stream recorder for this one without changing the rest of the flow.
 */
export function useRecordingComposite(deps: RecordingCompositeDeps) {
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const recorder = useClipRecorder();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const rafRef = useRef<number | null>(null);
  const compositeStreamRef = useRef<MediaStream | null>(null);
  /** Audio tracks borrowed from the source camera stream (not ours to stop). */
  const borrowedTracksRef = useRef<MediaStreamTrack[]>([]);

  const cancelLoop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (compositeStreamRef.current) {
      for (const track of compositeStreamRef.current.getTracks()) {
        if (!borrowedTracksRef.current.includes(track)) {
          track.stop();
        }
      }
      compositeStreamRef.current = null;
    }
    borrowedTracksRef.current = [];
  }, []);

  const drawFrame = useCallback(() => {
    const ctx = ctxRef.current;
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;

    const { getVideo, getLandmarks, getCelebrity, getRoundEnded } = depsRef.current;
    const state = useWorkoutStore.getState();
    const celebrity = getCelebrity();

    drawCompositeFrame(ctx, canvas.width, canvas.height, {
      video: getVideo(),
      landmarks: getLandmarks(),
      timer: {
        roundDurationSec: celebrity?.workoutDurationSec ?? 0,
        elapsedMs: state.elapsedMs,
        roundEnded: getRoundEnded(),
      },
      reps: state.reps,
      celebrity,
      celebrityReps: state.celebrityReps,
    });

    rafRef.current = requestAnimationFrame(drawFrame);
  }, []);

  const start = useCallback(
    (source: MediaStream) => {
      cancelLoop();

      const canvas = document.createElement("canvas");
      canvas.width = COMPOSITE_WIDTH;
      canvas.height = COMPOSITE_HEIGHT;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return;
      }

      const composite = canvas.captureStream(COMPOSITE_FPS);
      const borrowed: MediaStreamTrack[] = [];
      for (const track of source.getAudioTracks()) {
        composite.addTrack(track);
        borrowed.push(track);
      }

      canvasRef.current = canvas;
      ctxRef.current = ctx;
      compositeStreamRef.current = composite;
      borrowedTracksRef.current = borrowed;

      rafRef.current = requestAnimationFrame(drawFrame);
      recorder.start(composite);
    },
    [cancelLoop, drawFrame, recorder],
  );

  const stop = useCallback((): Promise<ClipRecordingResult | null> => {
    cancelLoop();
    return recorder.stop();
  }, [cancelLoop, recorder]);

  const reset = useCallback(() => {
    cancelLoop();
    recorder.reset();
  }, [cancelLoop, recorder]);

  // Never leave a draw loop (or recorder) running on unmount.
  useEffect(() => {
    return () => {
      cancelLoop();
    };
  }, [cancelLoop]);

  return {
    isRecording: recorder.isRecording,
    recordingSeconds: recorder.recordingSeconds,
    result: recorder.result,
    error: recorder.error,
    start,
    stop,
    reset,
    supported: recorder.supported,
  };
}
